import { NavLink, Outlet, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Compass, LineChart, Target, ListTodo, Calendar, Users, FileText, LayoutDashboard, LogOut, MessageSquare, KeyRound } from "lucide-react";
import { useState } from "react";
import AICoach from "@/pages/AICoach";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

const nav = [
  { to: "/app", icon: LayoutDashboard, label: "Overview", end: true },
  { to: "/app/vision", icon: Compass, label: "Vision / V-TO" },
  { to: "/app/scorecard", icon: LineChart, label: "Scorecard" },
  { to: "/app/rocks", icon: Target, label: "Rocks" },
  { to: "/app/issues", icon: ListTodo, label: "Issues" },
  { to: "/app/meetings", icon: Calendar, label: "Meetings" },
  { to: "/app/accountability", icon: Users, label: "Accountability" },
  { to: "/app/processes", icon: FileText, label: "Processes" },
];

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const [coachOpen, setCoachOpen] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const [pw, setPw] = useState({ current: "", next: "", confirm: "" });
  const [pwBusy, setPwBusy] = useState(false);

  const changePassword = async (e) => {
    e.preventDefault();
    if (pw.next !== pw.confirm) { toast.error("New passwords don't match"); return; }
    setPwBusy(true);
    try {
      await api.post("/auth/update", { password: pw.next, current_password: pw.current });
      toast.success("Password updated.");
      setPwOpen(false);
      setPw({ current: "", next: "", confirm: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update password");
    } finally { setPwBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#F9F9F8] text-neutral-900 flex">
      {/* Sidebar */}
      <aside className="w-60 border-r border-neutral-200 bg-white flex flex-col shrink-0">
        <Link to="/app" className="h-14 flex items-center gap-2 px-5 border-b border-neutral-200" data-testid="sidebar-brand">
          <div className="w-4 h-4 bg-[#E54D2E]" />
          <span className="font-display text-base font-bold">Runwell HQ</span>
        </Link>
        <div className="px-4 py-3 border-b border-neutral-200">
          <div className="mono text-[10px] text-neutral-500 uppercase tracking-wider">Company</div>
          <div className="font-display text-sm font-bold truncate" data-testid="sidebar-company-name">{user?.company}</div>
        </div>
        <nav className="flex-1 p-2 space-y-0.5">
          {nav.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={`nav-${n.label.toLowerCase().replace(/\W+/g,'-')}`}
              className={({ isActive }) =>
                `flex items-center gap-2.5 px-3 py-2 text-sm rounded-sm ${
                  isActive ? "bg-neutral-900 text-white" : "text-neutral-700 hover:bg-neutral-100"
                }`
              }
            >
              <n.icon className="h-4 w-4" strokeWidth={1.75} />
              <span>{n.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-neutral-200 space-y-2">
          <button
            onClick={() => setCoachOpen((s) => !s)}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm rounded-sm bg-[#E54D2E] text-white hover:bg-[#C93E22] btn-lift"
            data-testid="open-coach-btn"
          >
            <MessageSquare className="h-4 w-4" /> AI Ops Coach
          </button>
          <div className="flex items-center justify-between text-xs text-neutral-600 px-1">
            <span className="truncate" data-testid="sidebar-user-name">{user?.name}</span>
            <div className="flex items-center gap-2">
              <button onClick={() => setPwOpen(true)} className="hover:text-[#E54D2E]" title="Change password" data-testid="change-password-btn"><KeyRound className="h-3.5 w-3.5" /></button>
              <button onClick={logout} className="hover:text-[#E54D2E]" data-testid="logout-btn"><LogOut className="h-3.5 w-3.5" /></button>
            </div>
          </div>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0 flex flex-col">
        {/* Ticker */}
        <div className="ticker px-6 py-2 mono text-xs text-neutral-500 flex items-center gap-6 bg-white">
          <span className="text-[#E54D2E]">● LIVE</span>
          <span>WEEK OF {new Date().toISOString().slice(0,10)}</span>
          <span className="hidden md:inline">RHYTHM: WEEKLY L10 · QUARTERLY ROCKS · ANNUAL PLANNING</span>
        </div>
        <div className="flex-1 min-w-0">
          <Outlet />
        </div>
      </main>

      {/* Coach panel */}
      {coachOpen && <AICoach onClose={() => setCoachOpen(false)} />}

      <Dialog open={pwOpen} onOpenChange={setPwOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Change password</DialogTitle></DialogHeader>
          <form onSubmit={changePassword} className="space-y-3">
            <div>
              <Label htmlFor="pw-current">Current password</Label>
              <Input id="pw-current" type="password" required value={pw.current} onChange={(e)=>setPw({...pw,current:e.target.value})} className="rounded-sm mt-1" data-testid="change-pw-current-input" />
            </div>
            <div>
              <Label htmlFor="pw-next">New password</Label>
              <Input id="pw-next" type="password" required minLength={6} value={pw.next} onChange={(e)=>setPw({...pw,next:e.target.value})} className="rounded-sm mt-1" data-testid="change-pw-new-input" />
            </div>
            <div>
              <Label htmlFor="pw-confirm">Confirm new password</Label>
              <Input id="pw-confirm" type="password" required minLength={6} value={pw.confirm} onChange={(e)=>setPw({...pw,confirm:e.target.value})} className="rounded-sm mt-1" data-testid="change-pw-confirm-input" />
            </div>
            <Button type="submit" disabled={pwBusy} className="w-full bg-neutral-900 hover:bg-neutral-800 text-white rounded-sm" data-testid="change-pw-submit-btn">
              {pwBusy ? "Updating…" : "Update password"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
